import styled from '@emotion/styled';
import { IconButton, Typography } from '@material-ui/core';
import IncrementIcon from '@material-ui/icons/Add';
import DecrementIcon from '@material-ui/icons/Remove';
import { useCallback } from 'react';
import { createElement, FC } from 'react';

import { ProjectDetailsFragment, ProjectDetailsWithoutPricingDataFragment } from '../../../../../generated/graphql';
import theme from '../../../../../theme';
import { SPACING_UNIT } from '../../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../../styleUtility/flexibleLayout';
import { useEditingProjectState } from '../../hooks/useEditingProjectState';

type PassedProps = {
  project: ProjectDetailsFragment | ProjectDetailsWithoutPricingDataFragment | null;
  creationSetAmount: number;
  className?: string;
};

type ContainerProps = {
  minConceptCount: number;
  maxConceptCount: number;
  onChangeCreationSetAmount: ({ amount }: { amount: number }) => void;
};

type Props = PassedProps & ContainerProps;

const ContainerComponent: FC<PassedProps> = (props) => {
  const { updateCreationSetAmount } = useEditingProjectState();

  const onChangeCreationSetAmount = useCallback(
    ({ amount }: { amount: number }) => {
      updateCreationSetAmount(amount);
    },
    [updateCreationSetAmount],
  );

  const minConceptCount = props.project?.projectType.minConceptCount ?? 1;
  const maxConceptCount = props.project?.projectType.maxConceptCount ?? 100;

  return (
    <Component
      {...props}
      onChangeCreationSetAmount={onChangeCreationSetAmount}
      minConceptCount={minConceptCount}
      maxConceptCount={maxConceptCount}
    />
  );
};

const Component: FC<Props> = ({
  creationSetAmount,
  className,
  minConceptCount,
  maxConceptCount,
  onChangeCreationSetAmount,
}) => {
  const handleOnchangeAmount = (inputtedAmount: number) => {
    onChangeCreationSetAmount({
      amount: inputtedAmount,
    });
  };

  return (
    <Root className={className}>
      <CreationSetAmountControllerButton
        onClick={() => handleOnchangeAmount(creationSetAmount - 1)}
        disabled={creationSetAmount <= minConceptCount}
      >
        <DecrementIcon fontSize="inherit" />
      </CreationSetAmountControllerButton>
      <CreationSetAmount>
        <Typography variant="subtitle1">{creationSetAmount}</Typography>
      </CreationSetAmount>
      <CreationSetAmountControllerButton
        onClick={() => handleOnchangeAmount(creationSetAmount + 1)}
        disabled={creationSetAmount >= maxConceptCount}
      >
        <IncrementIcon fontSize="inherit" />
      </CreationSetAmountControllerButton>
    </Root>
  );
};

const Root = styled('div')`
  ${flexibleLayoutContainer({ direction: 'row', alignItems: 'center' })}
`;

const CreationSetAmount = styled('div')`
  ${flexibleLayoutContainer({
    alignItems: 'center',
    justifyContent: 'center',
  })}
  min-width: 66px;
  border-radius: 2px;
  border: 1px solid ${theme.palette.divider};
  padding: ${SPACING_UNIT}px;
`;

const CreationSetAmountControllerButton = styled(IconButton)`
  margin-left: ${SPACING_UNIT / 2}px;
  margin-right: ${SPACING_UNIT / 2}px;
  font-size: 16px;
  width: 32px;
  height: 32px;
`;

export const CreationSetAmountInput = ContainerComponent;
